import { create } from 'zustand';

/**
 * useTabletDentalChartStore — open/close state for the chairside paired
 * dental chart on coarse-pointer devices.
 *
 * Triggered by the dentist tapping the ProcedureWidget's right-edge
 * `Q-N · count` chip on iPad / Android tablets / phones. The desktop
 * (fine-pointer) branch and the non-dentist branch both bypass this
 * store and use the rail's `setSize('focused')` path as today.
 *
 * Variant M (chart + procedure rail paired) keeps the chart at
 * `presentation="tabletPaired"` alongside the rail. The per-tooth
 * summary moves to a `📋 Summary` button in the chart header; its
 * open/closed state lives here so both the host (to mount the overlay)
 * and the chart header (to toggle the button) can subscribe.
 *
 * State is intentionally tiny — one chart open at a time, tied to the
 * doctor's currently-active visit. The visit + viewer are captured at
 * `open()` time so the chart renders against the same patient even if
 * the parent re-renders between trigger and mount.
 */
const useTabletDentalChartStore = create((set) => ({
  isOpen: false,
  visit: null,
  viewer: null,
  // Per-tooth summary overlay (the `📋 Summary` button toggles this).
  // Lives inside the chart pane; covers only the chart, leaves the
  // procedure rail visible.
  summaryOpen: false,

  open: (visit, viewer) => {
    if (!visit) return;
    set({ isOpen: true, visit, viewer: viewer || null, summaryOpen: false });
  },

  close: () => set({ isOpen: false, visit: null, viewer: null, summaryOpen: false }),

  toggleSummary: () => set((s) => ({ summaryOpen: !s.summaryOpen })),
  closeSummary: () => set({ summaryOpen: false }),
}));

export default useTabletDentalChartStore;
